import type { BasketReportRow, SalesReportRow } from "@station/contracts";
import { Decimal, Money } from "@station/core";
import {
  type ChartOption,
  formatChartNumber,
  GRID,
  LEGEND_POSITION,
  VALUE_AXIS_LABEL,
} from "../fuel/chart-options.js";

/**
 * Market raporlarının saf mantığı (Prompt 16 — web, aşama B): grafik seçenekleri ve grafik özetleri. Hesap SUNUCUDADIR
 * (ciro KDV hariç, defter toplamı; SMM stok hareketinden); burada yalnızca ÇİZİM için kuruş → TL sayıya çevrilir
 * (AGENTS.md §3.2'nin izin verdiği tek yer — `chart-options.ts`).
 */

export const MARKET_REPORT_TABS = ["sales", "basket", "deadStock", "turnover", "expiring"] as const;
export type MarketReportTab = (typeof MARKET_REPORT_TABS)[number];

/** Grafikte en çok bu kadar kırılım çizilir (ürün kırılımında yüzlerce satır olabilir); tamamı tablodadır. */
export const CHART_TOP = 12;

function toMajor(minor: string): number {
  return Number(Money.fromMinor(minor).toMajorString());
}

/** Ciroya göre azalan ilk N satır (grafik için; tablo sunucunun sırasıyla kalır). */
export function topByRevenue<T extends Pick<SalesReportRow, "revenue">>(
  rows: readonly T[],
  limit = CHART_TOP,
): T[] {
  return [...rows].sort((a, b) => Money.fromMinor(b.revenue).cmp(Money.fromMinor(a.revenue))).slice(0, limit);
}

export interface SalesChartLabels {
  revenue: string;
  grossProfit: string;
  amountAxis: string;
}

/** Ciro + brüt kâr yatay sütun grafiği (kırılım adları uzun olabilir: yatay eksen okunaklı kalır). */
export function salesChartOption(rows: readonly SalesReportRow[], labels: SalesChartLabels): ChartOption {
  const top = topByRevenue(rows).reverse();
  return {
    grid: GRID,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: [labels.revenue, labels.grossProfit] },
    xAxis: { type: "value", name: labels.amountAxis, axisLabel: VALUE_AXIS_LABEL },
    yAxis: {
      type: "category",
      data: top.map((row) => row.label),
      axisLabel: { width: 160, overflow: "truncate" },
    },
    series: [
      { type: "bar", name: labels.revenue, barMaxWidth: 28, data: top.map((row) => toMajor(row.revenue)) },
      {
        type: "bar",
        name: labels.grossProfit,
        barMaxWidth: 28,
        data: top.map((row) => toMajor(row.grossProfit)),
      },
    ],
  };
}

export interface BasketChartLabels {
  averageBasket: string;
  conversion: string;
  amountAxis: string;
  percentAxis: string;
}

/** İstasyon başına sepet ortalaması (sütun, ₺) + akaryakıt → market dönüşüm oranı (çizgi, %; ikinci eksen). */
export function basketChartOption(rows: readonly BasketReportRow[], labels: BasketChartLabels): ChartOption {
  return {
    grid: GRID,
    tooltip: { trigger: "axis", valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: [labels.averageBasket, labels.conversion] },
    xAxis: { type: "category", data: rows.map((row) => row.stationName), axisLabel: { hideOverlap: true } },
    yAxis: [
      { type: "value", name: labels.amountAxis, axisLabel: VALUE_AXIS_LABEL },
      { type: "value", name: labels.percentAxis, axisLabel: VALUE_AXIS_LABEL, min: 0, max: 100 },
    ],
    series: [
      {
        type: "bar",
        name: labels.averageBasket,
        barMaxWidth: 64,
        data: rows.map((row) => (row.averageBasket === null ? null : toMajor(row.averageBasket))),
      },
      {
        type: "line",
        name: labels.conversion,
        yAxisIndex: 1,
        data: rows.map((row) =>
          row.conversionRatePercent === null
            ? null
            : Number(Decimal.from(row.conversionRatePercent).toFixed(2)),
        ),
      },
    ],
  };
}

/** Devir hızı / gün gösterimi: "4,25" / "7,1 gün"; null → tire. */
export function formatRatio(value: string | null, scale = 2): string {
  return value === null ? "—" : Decimal.from(value).formatTr(scale);
}
